import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { TopSelling } from "@/components/TopSelling";
import { FeaturedCodeBanner } from "@/components/FeaturedCodeBanner";
import { PromoBanner } from "@/components/PromoBanner";
import { Flyer } from "@/components/Flyer";
import { BlogSection } from "@/components/BlogSection";
import { RoutineSection, WhySection, LoyaltySection, StoresSection, ReviewsSection, ShopOnlineSection } from "@/components/LandingSections";
import { Seo, localBusinessJsonLd } from "@/components/Seo";
import { useStoreInfo } from "@/context/StoreInfoContext";

const DEFAULT_BESTSELLER = {
  name: "WheyJoy 900g",
  image_url: "https://customer-assets.emergentagent.com/job_shopping-list-app-17/artifacts/1gty2sys_MOCKUP_BLACK_900.jpg",
  product_id: null,
};

export default function Landing() {
  const [bestseller, setBestseller] = useState(DEFAULT_BESTSELLER);
  const { info } = useStoreInfo();

  useEffect(() => {
    api.get("/bestseller").then(({ data }) => {
      if (data && data.product_id) setBestseller(data);
    }).catch(() => {});
  }, []);

  return (
    <div className="bg-[#0A0A0A] text-white">
      <Seo
        id="home"
        description="Power Fitness Nutrition: negozio di integratori sportivi in Sicilia, a San Cataldo (CL) e Canicattì (AG). Proteine WheyJoy, creatina, pre-workout, barrette. Ritiro in negozio o consegna in 2-4 giorni in tutta la Sicilia."
        jsonLd={localBusinessJsonLd(info)}
      />
      {/* HERO */}
      <section className="relative min-h-[85vh] overflow-hidden grain">
        <div className="absolute inset-0">
          <picture>
            <source media="(max-width: 767px)" srcSet="/hero-wheyjoy-m.webp" type="image/webp" />
            <source srcSet="/hero-wheyjoy.webp" type="image/webp" />
            <img
              src="/hero-wheyjoy.jpg"
              alt="WheyJoy — Power Fitness Nutrition"
              width="1264" height="848"
              fetchpriority="high"
              decoding="async"
              className="w-full h-full object-cover object-[82%_40%] lg:object-[center_40%]"
              data-testid="hero-image"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/20 to-[#0A0A0A]/50" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/85 to-[#0A0A0A]/30 lg:via-[#0A0A0A]/40 lg:to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 min-h-[85vh] items-center">
          <div className="lg:col-span-7">
            <div className="font-overline text-xs text-[#D4AF37] mb-6 flex items-center gap-3" data-testid="hero-badge">
              <span className="w-8 h-px bg-[#D4AF37]"></span> NUOVA COLLEZIONE 2026
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter mb-6" data-testid="hero-title">
              POWER FITNESS<br />
              <span className="text-[#D4AF37]">NUTRITION</span>
            </h1>
            <p className="font-display font-bold text-xl sm:text-2xl text-white leading-tight mb-3" data-testid="hero-tagline">
              La tua nutrizione. <span className="text-[#D4AF37]">Il tuo prossimo livello.</span>
            </p>
            <p className="text-base sm:text-lg text-neutral-400 max-w-xl leading-relaxed mb-8" data-testid="hero-subtitle">
              Integratori selezionati per allenarti, recuperare e raggiungere i tuoi obiettivi.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild className="bg-[#D4AF37] hover:bg-[#E5C158] text-black rounded-none font-overline text-xs px-8 h-12" data-testid="cta-shop-now">
                <Link to="/catalog">Acquista online <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white hover:text-black rounded-none font-overline text-xs px-8 h-12" data-testid="cta-blog">
                <Link to="/blog">Consigli & Guide</Link>
              </Button>
            </div>
            <p className="mt-4 text-sm text-neutral-300" data-testid="hero-shop-hint">
              <span className="text-[#D4AF37] font-bold">Spedizione gratis sopra €39</span> · consegna in 2-4 giorni · anche senza registrazione
            </p>
          </div>

          <div className="lg:col-span-5 hidden lg:flex items-end justify-end h-full">
            <Link to={bestseller.product_id ? `/product/${bestseller.product_id}` : "/catalog"} className="group flex items-center gap-4 bg-black/70 backdrop-blur-md border border-[#D4AF37]/40 hover:border-[#D4AF37] p-4 pr-6 transition-colors" data-testid="hero-bestseller">
              <div className="w-16 h-16 bg-[#141414] overflow-hidden shrink-0">
                <img
                  src={imgUrl(bestseller.image_url, 800)} loading="lazy" decoding="async"
                  alt={bestseller.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={(e) => { e.target.src = DEFAULT_BESTSELLER.image_url; }}
                />
              </div>
              <div>
                <div className="font-overline text-[9px] text-[#D4AF37]">BESTSELLER</div>
                <div className="font-display font-bold text-lg leading-tight">{bestseller.name}</div>
                <div className="text-[10px] text-neutral-400 group-hover:text-white transition-colors flex items-center gap-1 mt-0.5">Scopri <ArrowRight className="w-3 h-3" /></div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <FeaturedCodeBanner />

      {/* MARQUEE */}
      <div className="relative overflow-hidden border-y border-white/10 py-6 bg-black">
          <div className="flex animate-marquee whitespace-nowrap">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="flex items-center gap-12 px-6 font-display font-black text-4xl uppercase tracking-tight">
                <span>Proteine</span><span className="text-[#D4AF37]">★</span>
                <span>Barrette</span><span className="text-[#D4AF37]">★</span>
                <span>Creatina</span><span className="text-[#D4AF37]">★</span>
                <span>Carboidrati & Endurance</span><span className="text-[#D4AF37]">★</span>
                <span>Pre-Workout</span><span className="text-[#D4AF37]">★</span>
                <span>Testosterone</span><span className="text-[#D4AF37]">★</span>
                <span>Vitamine</span><span className="text-[#D4AF37]">★</span>
                <span>Keto</span><span className="text-[#D4AF37]">★</span>
                <span>Food</span><span className="text-[#D4AF37]">★</span>
                <span>Salute</span><span className="text-[#D4AF37]">★</span>
                <span>Abbigliamento</span><span className="text-[#D4AF37]">★</span>
              </div>
            ))}
          </div>
      </div>

      {/* PROMO BANNER (promo attive scorrevoli) */}
      <PromoBanner />

      {/* TOP SELLING (TOP 3 vendite reali) */}
      <TopSelling />

      {/* VOLANTONE (offerte in griglia, gestito dall'admin) */}
      <Flyer />

      {/* ACQUISTA ONLINE */}
      <ShopOnlineSection />

      {/* TROVA LA TUA ROUTINE */}
      <RoutineSection />

      {/* PERCHÉ POWER FITNESS NUTRITION */}
      <WhySection />

      {/* PROGRAMMA FEDELTÀ */}
      <LoyaltySection />

      {/* PUNTI VENDITA */}
      <StoresSection />

      {/* RECENSIONI */}
      <ReviewsSection />

      {/* DAL BLOG */}
      <BlogSection />

      {/* BIG CTA */}
      <section className="relative py-24 border-t border-white/10 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="outline-text font-display font-black text-[9vw] leading-none whitespace-nowrap">POWER FITNESS NUTRITION</div>
        </div>
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h3 className="font-display font-black text-4xl lg:text-5xl tracking-tight mb-4">PRONTO A FARE SUL SERIO?</h3>
          <p className="text-neutral-400 mb-8 max-w-xl mx-auto">Crea il tuo account, accumula punti e sblocca vantaggi esclusivi.</p>
          <Button asChild className="bg-[#D4AF37] hover:bg-[#E5C158] text-white rounded-none font-overline text-xs px-10 h-12" data-testid="cta-register">
            <Link to="/register">Inizia Adesso</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
